// Public surface for `@auctogon/time` — one model for time, built once.
//
// Every export here is pure + client-safe (no node built-ins, no `server-only`,
// no React, native `Intl` only), so re-exporting everything from this root
// barrel is safe per sharing.md §3 root-barrel hygiene — there is no server-only
// module that could leak into a client (or React Native) bundle.

export type {
    TimeInput,
    CalendarDate,
    DisplayContext,
    ZoneAbbrevStyle,
    CountdownStatus,
    DurationParts,
    TimeRemaining,
    DisplayZone,
} from './types';

export { formatEventTime } from './format-event-time';
export type { FormatEventTimeOptions } from './format-event-time';
export { formatEventTimeRange } from './format-event-time-range';
export type { FormatEventTimeRangeOptions } from './format-event-time-range';

export { formatInZone } from './format-in-zone';

export { formatLogicalDate } from './format-logical-date';
export type { FormatLogicalDateOptions } from './format-logical-date';

// Logical-date (calendar-date) parts <-> `YYYY-MM-DD`. The canonical home for
// date-only arithmetic: never parse one with `new Date(str)` and never
// serialize one with `.toISOString()` — both force a zone onto a value that has
// none (ADR 024). Enforced by `logicaldate/no-logical-date-instant-roundtrip`.
export { parseLogicalDate } from './parse-logical-date';
export { toLogicalDateString } from './to-logical-date-string';
export { todayLogicalDate } from './today-logical-date';
export { addLogicalDays } from './add-logical-days';
export { addWeekdays } from './add-weekdays';
export { diffLogicalDays } from './diff-logical-days';
export { utcCalendarDateOfTimestampText } from './utc-calendar-date-of-timestamp-text';

export { formatRelative } from './format-relative';
export type { FormatRelativeOptions } from './format-relative';

export { deriveZoneAbbrev } from './derive-zone-abbrev';

export { isValidTimeZone } from './is-valid-time-zone';

export { carriesZoneOffset } from './carries-zone-offset';

export { resolveDisplayZone } from './resolve-display-zone';
export type { ResolveDisplayZoneInput } from './resolve-display-zone';

export { isSameDayInZone } from './is-same-day-in-zone';
export { todayOrDatedFormat } from './today-or-dated-format';

export { isSameInstant } from './is-same-instant';

export { instantToWallTime } from './instant-to-wall-time';
export type { WallTime } from './instant-to-wall-time';

export { wallTimeToInstant } from './wall-time-to-instant';

export { calendarDayRangeToInstants } from './calendar-day-range-to-instants';
export type { CalendarDayInstantRange } from './calendar-day-range-to-instants';

export { timeRemaining, breakDuration } from './time-remaining';

export { formatDuration } from './format-duration';

export { createServerClock } from './server-clock';
export type { ServerClock, ServerTimeFetcher } from './server-clock';

export { COUNTDOWN_WINDOW_MS, CLOSING_SOON_MS } from './close-windows';

export { deriveCloseUrgency } from './derive-close-urgency';
export type {
    CloseUrgencyVerdict,
    CloseUrgencyForm,
    CloseUrgencyBand,
    DeriveCloseUrgencyInput,
} from './derive-close-urgency';
